import { AccountController } from "@/app/ui/controllers/AccountController";
import { FlagsPermissionBookingV3 } from "@/feature-flags/flag-booking-v3";
import { FlagsBcsBulkDownloadTemplate } from "@/feature-flags/flags-bulk-download-template";
import { FlagsMedusa } from "@/feature-flags/flags-misc-medusa";

export const permissionBulkBooking = () => {
  return {
    idAccount: AccountController.accountData.account_id,
    permissionPos: AccountController.accountData.isPosAccount,
    permissionClient: AccountController.accountData.isAllClient,
    permissionInternal: AccountController.accountData.isInternalAccount,
    permissionPosClient: FlagsPermissionBookingV3.permission_booking_v3_create_client.isEnabled()
  };
};

export type TemplateAudience = "client" | "retail";
export type TemplateScope = "international" | "domestic";
export type SupportedCountry = "id" | "my";

export interface BulkTemplateUrlParams {
  audience?: TemplateAudience;
  scope: TemplateScope;
  countryCode?: string | null;
}

export const normalizeCountryCode = (
  countryCode?: string | null
): SupportedCountry => {
  return (countryCode || "").toLowerCase() === "my" ? "my" : "id";
};

export const resolveTemplateLocaleSuffix = (
  countryCode?: string | null
): "en" | "id" => {
  return normalizeCountryCode(countryCode) === "my" ? "en" : "id";
};

// POS locale must come from the counter's district, not the account's own
// country_code (AccountMapper prioritizes data.country_code over
// partner_location.country.code, which is wrong for a POS counter abroad).
export const resolvePosDistrictCountryCode = (
  partnerLocation?: any
): string => {
  return partnerLocation?.country?.code || "";
};

export const woodpackingFlow = (): "" | "v1" | "v2" => {
  const val = FlagsMedusa.flag_booking_woodpacking_enable_for_malaysia.getValue();
  return val === "v1" || val === "v2" ? val : "";
};

const TEMPLATE_STRATEGIES: Record<
  TemplateScope,
  Record<
    TemplateAudience,
    (country: SupportedCountry, bucket: string, bucketAnnouncement: string) => string
  >
> = {
  international: {
    retail: (country, bucket) => {
      const suffix = country === "my" ? "en" : "id";
      return `${bucket}/bulk-booking-international-retail-${suffix}.xlsx`;
    },
    client: (country, bucket) => {
      const suffix = country === "my" ? "en" : "id";
      return `${bucket}/bulk-booking-international-client-${suffix}.xlsx`;
    }
  },
  domestic: {
    retail: (country, bucket, bucketAnnouncement) =>
      country === "my"
        ? `${bucketAnnouncement}/template/Template+booking+retail+-+EN${woodpackingFlow() === "v2" ? "-v2" : ""}.xlsx`
        : `${bucket}/bulk-booking-template-retail${woodpackingFlow() === "v2" ? "-v2" : ""}.xlsx`,
    client: (country, bucket, bucketAnnouncement) =>
      country === "my"
        ? `${bucketAnnouncement}/template/Template+booking+klien+-+EN.xlsx`
        : `${bucket}/bulk-booking-domestic-id.xlsx`
  }
};

export const bulkTemplateUrl = ({
  audience = "retail",
  scope,
  countryCode
}: BulkTemplateUrlParams): string => {
  const bucket = FlagsBcsBulkDownloadTemplate.config_bucket_storage.getValue() || "";
  const bucketAnnouncement = FlagsBcsBulkDownloadTemplate.config_bucket_storage_announcement.getValue() || "";
  const country = normalizeCountryCode(countryCode);

  const builder = TEMPLATE_STRATEGIES[scope]?.[audience];
  return builder ? builder(country, bucket, bucketAnnouncement) : "";
};

export const internationalTemplateUrl = (
  countryCode?: string | null
): string => {
  return bulkTemplateUrl({ audience: "retail", scope: "international", countryCode });
};

export const resolveInternationalTemplateUrl = (
  countryCode?: string | null
): string => {
  return bulkTemplateUrl({ audience: "retail", scope: "international", countryCode });
};